'use strict';

/**
 * Store → payload conversion. RPI-1031.
 *
 * The single definition of how an AnswerStore value becomes the string the form
 * would submit. RR-4's shadow compare and the future RR-5 store-driven submit
 * both read through here, so "would RR-5 send the same bytes?" is measured with
 * the exact function RR-5 will use — not a second copy that can drift.
 *
 * Payload representation contract:
 *   - undefined / null  -> ''             an absent or unset variable submits ''
 *   - string            -> the string     verbatim, already payload-encoded
 *   - object            -> JSON.stringify  no form control produces one; make it visible
 *   - anything else     -> String(value)
 *
 * `buildStorePayload` reads each DECLARED variable individually with
 * `store.get`, never `store.getAll()`: the payload is defined by the page's
 * declared variables (`page_state.allvars`), not by whatever keys happen to be
 * in the store. `page_state.vardata` is overlaid by the submit caller
 * (`events.js`), exactly as the DOM path does it — not here.
 */

/**
 * The value one store entry contributes to a payload.
 * @param {*} value
 * @returns {string}
 */
export function toPayloadValue(value) {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  } catch (e) {
    return String(value);
  }
}

/**
 * Build the payload a store-driven submit (RR-5) would send from the store.
 *
 * @param {Array<string>} allvars The page's declared variables
 *     (`page_state.allvars`).
 * @param {Object} store An AnswerStore exposing `get(name)` (e.g.
 *     `Gryphon.answers`).
 * @returns {Object} Map of variable name to submitted string — one entry per
 *     declared variable.
 */
export function buildStorePayload(allvars, store) {
  var out = {};
  var names = allvars || [];
  for (var i = 0; i < names.length; i++) {
    var name = names[i];
    out[name] = toPayloadValue(store ? store.get(name) : undefined);
  }
  return out;
}
